import { useState } from 'react';
import type { PlatformId } from '@utils/types/connections/connection.ts';
import type {
  PublishTarget,
  YouTubeNewVideoTarget
} from '@utils/types/publishing/publish.ts';
import StepChooseTargetNewVideo from './step-choose-target-new-video';
import StepChooseTargetModeTabs, {
  type TargetMode
} from './step-choose-target-mode-tabs';
import ReconnectForUploadNotice from './reconnect-for-upload-notice';
import StepChooseTargetExisting from './step-choose-target-existing';

interface Props {
  platform: PlatformId;
  /**
   * True when the org has opted into YouTube video uploads (#4101 Step 2).
   * Hides the "Upload as new video" tab when off — the backend would
   * reject the job anyway because the connection lacks the upload
   * scope, so showing it would be a dead-end for the user.
   */
  allowNewVideo?: boolean;
  /**
   * True when the chosen connection carries the ``youtube.upload``
   * OAuth scope. False means the admin flipped the org setting on
   * AFTER this connection was authorized — the UI prompts a
   * reconnect instead of letting the user confirm a job that would
   * fail at ``videos.insert`` with 403.
   */
  connectionHasUploadScope?: boolean;
  /** Called when the user clicks "Reconnect to enable uploads". */
  onReconnectForUpload?: () => void;
  /**
   * True once Google's YouTube API services audit has cleared for
   * this deployment. Controls whether the privacy picker offers the
   * ``public`` radio — the connector clamps it to private either way
   * when the audit is still pending, so letting the user tick it
   * would be a lie.
   */
  publicPrivacyAvailable?: boolean;
  targets: PublishTarget[];
  isLoading: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  selectedId: string | null;
  agendaItemId: string | null;
  /** Currently selected target. Lets us keep new-video form input in sync. */
  selectedTarget: PublishTarget | null;
  onPick: (target: PublishTarget, agendaItemId?: string) => void;
  /** True when the backend has a next page. Caller fetches on request. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

function emptyNewVideoTarget(): YouTubeNewVideoTarget {
  return {
    kind: 'youtube_new_video',
    id: '',
    title: '',
    description: '',
    privacy: 'private'
  };
}

/**
 * Users pick a target on the connected platform. YouTube has two paths —
 * add captions to an existing video, or upload the session's source
 * media as a new video (#4101 Step 2); the toggle lives at the top. The
 * iBabs layout stays single-mode because meetings are not something we
 * create from Scriptix.
 */
export default function StepChooseTarget({
  platform,
  allowNewVideo = false,
  connectionHasUploadScope = true,
  onReconnectForUpload,
  publicPrivacyAvailable = false,
  targets,
  isLoading,
  query,
  onQueryChange,
  selectedId,
  agendaItemId,
  selectedTarget,
  onPick,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage
}: Props) {
  const supportsNewVideo = platform === 'youtube' && allowNewVideo;
  const [mode, setMode] = useState<TargetMode>(() =>
    selectedTarget?.kind === 'youtube_new_video' ? 'new-video' : 'existing'
  );

  const switchMode = (next: TargetMode) => {
    if (next === mode) return;
    setMode(next);
    // Clearing the picked target on switch avoids a stale selection
    // leaking into the confirm step when the user flips modes.
    if (next === 'new-video') {
      onPick(emptyNewVideoTarget());
    } else {
      // Falling back to a radio-cleared state — the user will pick a
      // target from the list to re-select.
      onPick({ kind: 'youtube_video', id: '', title: '' });
    }
  };

  const newVideoDraft: YouTubeNewVideoTarget =
    selectedTarget?.kind === 'youtube_new_video'
      ? selectedTarget
      : emptyNewVideoTarget();

  return (
    <div className="flex flex-col gap-3">
      {supportsNewVideo && (
        <StepChooseTargetModeTabs mode={mode} onChange={switchMode} />
      )}

      {mode === 'new-video' ? (
        connectionHasUploadScope ? (
          <StepChooseTargetNewVideo
            draft={newVideoDraft}
            onChange={onPick}
            publicAllowed={publicPrivacyAvailable}
          />
        ) : (
          <ReconnectForUploadNotice onReconnect={onReconnectForUpload} />
        )
      ) : (
        <StepChooseTargetExisting
          targets={targets}
          isLoading={isLoading}
          query={query}
          onQueryChange={onQueryChange}
          selectedId={selectedId}
          agendaItemId={agendaItemId}
          onPick={onPick}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
}
